import { useEffect } from 'react';
import { create } from 'zustand';
import { api, renewSession } from '@/api/client';
import { personalStore } from '@/offline/session';
import { onWindowMessage, tellOtherWindows } from './channel';

/**
 * Locks the app after a spell with nobody using it, the way a desktop ERP
 * does on a shared office PC. The screen is covered and the password is asked
 * again; nothing is signed out and nothing typed is lost.
 *
 * Kept in localStorage so a reload cannot be used to skip the lock, and
 * shared between windows: activity in any window keeps them all awake, and
 * locking or unlocking one does the same to the rest.
 */
const LOCKED_KEY = 'poultry.locked';

interface LockState {
  locked: boolean;
  lock: () => void;
  /** Checks the password with the server; throws with its message when wrong. */
  unlock: (password: string) => Promise<void>;
}

function readLocked(): boolean {
  try {
    return localStorage.getItem(LOCKED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeLocked(locked: boolean): void {
  try {
    if (locked) localStorage.setItem(LOCKED_KEY, '1');
    else localStorage.removeItem(LOCKED_KEY);
  } catch {
    // Private mode: the lock still holds for this window's lifetime.
  }
}

export const useLock = create<LockState>((set) => ({
  locked: readLocked(),

  lock: () => {
    writeLocked(true);
    set({ locked: true });
    tellOtherWindows({ type: 'lock' });
  },

  unlock: async (password) => {
    // The lock may have lasted longer than the access token. Renew it first;
    // if the session itself has ended, the app goes back to the sign-in screen.
    const session = await renewSession();
    if (session.status === 'unreachable') {
      // No server to ask: the copy of this person's work on the PC opens only
      // with their password, so it can check it instead (.docs/023).
      const copy = personalStore();
      if (!copy) {
        throw new Error(
          'No connection, so the password cannot be checked. Nothing you typed is lost — unlock once the connection is back.',
        );
      }
      if (!(await copy.passwordOpens(password))) throw new Error('The password is not right.');
      unlocked();
      return;
    }
    if (session.status === 'expired') {
      // renewSession() has already ended the local session and put the
      // sign-in screen's notice up, with whatever reason the server gave;
      // this error is only what the unlock form itself shows before the
      // screen underneath it changes.
      throw new Error(session.reason ?? 'Your session has ended. Please sign in again.');
    }
    await api.post('/api/v1/auth/unlock', { password });
    unlocked();
  },
}));

function unlocked(): void {
  writeLocked(false);
  lastActivity = Date.now();
  useLock.setState({ locked: false });
  tellOtherWindows({ type: 'unlock' });
}

/** Clears the lock without a password — only at sign-out, when there is nothing left to protect. */
export function clearLock(): void {
  writeLocked(false);
  useLock.setState({ locked: false });
}

let lastActivity = Date.now();
/** Other windows hear about activity at most this often. */
const SHARE_EVERY_MS = 30_000;
let lastShared = 0;

function noteActivity(): void {
  lastActivity = Date.now();
  if (lastActivity - lastShared > SHARE_EVERY_MS) {
    lastShared = lastActivity;
    tellOtherWindows({ type: 'activity', at: lastActivity });
  }
}

onWindowMessage((m) => {
  if (m.type === 'lock') {
    writeLocked(true);
    useLock.setState({ locked: true });
  } else if (m.type === 'unlock') {
    lastActivity = Date.now();
    useLock.setState({ locked: false });
  } else if (m.type === 'activity') {
    lastActivity = Math.max(lastActivity, m.at);
  }
});

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'pointermove'] as const;

/**
 * Watches for input and locks after `minutes` without any. 0 turns it off.
 * Mounted once by the signed-in shell.
 */
export function useIdleLock(minutes: number, enabled: boolean): void {
  useEffect(() => {
    if (!enabled || minutes <= 0) return;
    lastActivity = Date.now();
    for (const e of ACTIVITY_EVENTS) window.addEventListener(e, noteActivity, { passive: true });
    const timer = setInterval(() => {
      if (!useLock.getState().locked && Date.now() - lastActivity >= minutes * 60_000) {
        useLock.getState().lock();
      }
    }, 5_000);
    return () => {
      for (const e of ACTIVITY_EVENTS) window.removeEventListener(e, noteActivity);
      clearInterval(timer);
    };
  }, [minutes, enabled]);
}
